<!-- eslint-disable vue-a11y/form-has-label -->
<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<div
		ref="mainElement"
		class="c-NumberPicker"
		:class="mainClassObj"
		@focus.capture="toggleFocus(true)"
		@blur.capture="toggleFocus(false)">
		<span
			class="c-NumberPicker__label"
			@click.self="setFocus">{{ label }}</span>
		<div class="c-NumberPicker__controlsBlock">
			<button
				type="button"
				class="up"
				title="Add step number"
				:disabled="disabled"
				@click="changeStep(true)">
				<i></i>
			</button>
			<button
				type="button"
				class="down"
				title="Subtract step number"
				:disabled="disabled"
				@click="changeStep(false)">
				<i></i>
			</button>
		</div>
		<input
			ref="input"
			v-model="model"
			class="c-NumberPicker__input"
			type="number"
			min="0"
			:disabled="disabled"
			:step="step"
			@change="handleChange"
			@keydown="handleKeyEvents">
	</div>
</template>

<script>
import { debounce } from '../../utils/utils'

export default {
	name: 'NumberPicker',
	props: {
		/**
     * The NumberPicker label
     */
		label: {
			type: String,
			required: true,
			default: ''
		},
		/**
     * The NumberPicker data variable
     */
		data: {
			type: Number,
			required: false,
			default: 0
		},
		/**
     * The NumberPicker step value
     */
		step: {
			type: Number,
			required: false,
			default: 100
		},
		/**
     * If the NumberPicker is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: false
		},
		/**
     * If the NumberPicker is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data () {
		return {
			isFocused: false,
			model: 0
		}
	},
	computed: {
		mainClassObj () {
			return {
				focus: this.isFocused,
				disabled: this.disabled,
				isActive: this.isActive,
				withValue: this.model >= 0
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				this.model = parseInt(newVal, 10)
			},
			immediate: true
		}
	},
	created () {
		this.emitChange = debounce(this.emitChange, 500)
	},
	methods: {
		toggleFocus (arg) {
			this.isFocused = arg
		},
		setFocus () {
			this.$refs.input.focus()
		},
		changeStep (stepUp) {
			// eslint-disable-next-line no-restricted-globals
			this.model = isNaN(parseInt(this.model, 10)) ? 0 : parseInt(this.model, 10)
			if (stepUp) {
				this.model += this.step
			} else if (this.model - this.step < 0) {
				this.model = 0
			} else {
				this.model -= this.step
			}
			this.emitChange(this.model)
		},
		emitChange () {
			/**
       * Triggers when NumberPicker changes it's value
       * @event change
       * @property {number} model new value set
       */
			this.$emit('change', this.model)
		},
		handleChange () {
			// eslint-disable-next-line no-restricted-globals
			this.model = isNaN(parseInt(this.model, 10)) ? 0 : parseInt(this.model, 10)
			this.$emit('change', this.model)
		},
		handleKeyEvents (e) {
			const allowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'ArrowLeft', 'ArrowRight', 'Backspace', 'delete', 'Escape', 'Tab']
			if (allowed.indexOf(e.key) === -1) {
				e.preventDefault()
				e.stopPropagation()
				if (e.key === 'ArrowUp') {
					this.changeStep(true)
				} else if (e.key === 'ArrowDown') {
					this.changeStep(false)
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-NumberPicker {
  $font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;

  border: 1px solid $un-gray-light;
  height: 50px;
  border-radius: $border-radius;
  background-color: $un-white;
  position: relative;
  cursor: pointer;

  &::-moz-focus-inner {
    border: 0;
  }

  &.disabled {
    border-color: $un-n500;
    background-color: $un-n100;
		cursor: not-allowed;

    & .c-NumberPicker__label,
    & .c-NumberPicker__input {
      color: $un-n500;
			cursor: not-allowed;
    }

    & .c-NumberPicker__controlsBlock button {
      background-color: $un-n100;

      i {
        background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%238D98AA" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
      }
    }
  }

  &.focus {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;

    .c-NumberPicker__label {
      color: $un-blue;
      top: calc(#{$base-padding} / 2) !important;
      font-size: $font-size-label-top !important;
    }
  }

	&.isActive {
		border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;

    .c-NumberPicker__label {
      color: $un-blue;
    }
	}

  &.withValue {
    .c-NumberPicker__label {
      top: calc(#{$base-padding} / 2) !important;
      font-size: $font-size-label-top !important;
    }
  }

  &__label {
    display: block;
    font-size: $font-size-label;
    color: $un-n700;
    position: absolute;
    top: 14px;
    left: calc(#{$base-padding / 2});
    transition: all $default-time $default-easing;
  }

  &__input {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    background-color: transparent;
    font-family: 'Open Sans', Helvetica, Arial, sans-serif;
    font-size: $font-size-value;
    line-height: $font-size-value;
    color: $un-n700;
    padding: 22px calc(#{$base-padding / 2}) calc(#{$base-padding / 2}) calc(#{$base-padding / 2});
    box-sizing: border-box;
    cursor: pointer;

		&:-moz-ui-invalid, &:invalid {
      box-shadow: none;
    }
  }

  &__controlsBlock {
    position: absolute;
    top: 1px;
    right: 1px;
    width: 38px;
    height: calc(100% - 2px);
    background-color: $un-white;
    border-top-right-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
    display: flex;
    flex-flow: column;

    button {
      width: 100%;
      height: 50%;
      outline: none;
      border: none;
      background-color: transparent;

      &::-moz-focus-inner {
        border: 0;
      }

      &:focus {
        border: 1px solid $un-blue;
      }

      i {
        width: 100%;
        height: 100%;
        display: block;
        background-position: center;
        background-repeat: no-repeat;
        background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%23343F50" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
      }

      &.up i {
        transform: rotate(180deg);
        margin-left: -2px;
      }
    }
  }
}
</style>
